<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import { useGetReportSectionsTree } from "@/stores/queries";

const props = defineProps<{
  modelValue: boolean;
  reportId: number;
  title?: string;
  hideUnclassified?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "select", sectionId: number | null): void;
}>();

const { t } = useTranslation();

const reportIdRef = computed(() => props.reportId);
const { data: sectionsTree } = useGetReportSectionsTree(reportIdRef);

// Flatten sections
type SectionItem = {
  id: number;
  path?: string;
  title: string;
  children?: SectionItem[];
};

const flattenSections = (items: SectionItem[]): SectionItem[] => {
  const result: SectionItem[] = [];
  for (const item of items) {
    result.push(item);
    if (item.children?.length) {
      result.push(...flattenSections(item.children));
    }
  }
  return result;
};

const flatSections = computed(() => flattenSections(sectionsTree.value || []));

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const handleSelect = (sectionId: number | null) => {
  emit("select", sectionId);
  dialogVisible.value = false;
};
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    :title="title ?? t('report:photos.moveDialog.title')"
    width="400px"
    append-to-body
  >
    <div class="move-section-list">
      <div
        v-if="!hideUnclassified"
        class="move-section-item p-2 rounded mb-1"
        @click="handleSelect(null)"
      >
        <el-icon class="me-2"><i class="bi-inbox"></i></el-icon>
        {{ t("report:photos.sorting.unclassified") }}
      </div>
      <div
        v-for="section in flatSections"
        :key="section.id"
        class="move-section-item p-2 rounded mb-1"
        @click="handleSelect(section.id)"
      >
        <span class="section-text"
          >{{ section.path }}. {{ section.title }}</span
        >
      </div>
    </div>
  </el-dialog>
</template>

<style lang="scss" scoped>
.move-section-list {
  max-height: 400px;
  overflow-y: auto;
}

.move-section-item {
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: var(--bs-light);
  }
}

.section-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
